import { FormBuilder, IFormConfig } from '@fareye/form-builder'
import { Button, Flex, IRenderControl, toast } from '@fareye/ui'
import {
  cancelToken,
  cloneDeep,
  get,
  isEmpty,
  post,
  put,
  differenceWith,
  isEqual
} from '@fareye/utils'
import React from 'react'
import { Country, State } from 'country-state-city'
import { HEADER_INFLECTION } from '../../config/constants'
import { convertToNestedBasedOnSeparator, filterKeyFromObject, getErrorMessage } from '../../utility/funcs'
import { IChildUserConfig, IModuleRenderingConfig, ISpecificKey, TChildUserConfigs } from '../../utility/types'
import { DataListing } from '../ChildUserFilter/components/DataListing'

const CustomFormWrapper = ({ config, formDefaultValue, formGroupName, isLoading, baseUrl, setSubmitValues, edit }: any) => {
  const [formValues, setFormValues] = React.useState<any>({})
  const [countryOptions, setCountryOptions] = React.useState<{ label: string; value: string }[]>([])
  React.useEffect(() => {
    const countries = Country.getAllCountries().map(c => ({
      label: c.name,
      value: c.name
    }))
    setCountryOptions(countries)
  }, [])

  const handleFormChange = (formikProps: any) => {
    const values = formikProps.values
    const oauthKey = edit ? 'useOauth2' : 'consigner-userAttributes-useOauth2'
    const demoKey = edit ? 'demoAccount' : 'consigner-demoAccount'
    const replicateKey = edit ? 'replicateData' : 'consigner-replicateData'
    const replicateFromKey = edit ? 'replicateFrom' : 'consigner-replicateFrom'
    if (!values[demoKey] && values[replicateKey]) {
      formikProps.setFieldValue(replicateKey, false)
    }
    if (!values[replicateKey] && values[replicateFromKey]) {
      formikProps.setFieldValue(replicateFromKey, null)
    }
    setFormValues(values)
  }
  const getModifiedConfig = () => {
    const updated = cloneDeep(config)
    const oauthKey = edit ? 'useOauth2' : 'consigner-userAttributes-useOauth2'
    const demoKey = edit ? 'demoAccount' : 'consigner-demoAccount'
    const replicateKey = edit ? 'replicateData' : 'consigner-replicateData'
    const replicateFromKey = edit ? 'replicateFrom' : 'consigner-replicateFrom'

    Object.values(updated.formConfig || {}).forEach((row: any) => {
      row.data.forEach((field: any) => {
        if (
          [
              'feEmail',
              'feUsername',
              'userCode',
              'consigner-userAttributes-feEmail',
              'consigner-userAttributes-feUsername',
              'consigner-userAttributes-userCode'
            ].includes(field.formKey)
        ) {
          field.isHidden = !formValues[oauthKey]
        }
        if (field.formKey === replicateKey) {
          field.isHidden = !formValues[demoKey]
        }
        if (field.formKey === replicateFromKey) {
          field.isHidden = !formValues[replicateKey]
        }
        if (field.formKey === 'consigner-country') {
          field.options = countryOptions
            field.type = 'select'
            field.apiUrl = undefined
        }
        if (field.formKey === 'consigner-state') {
          const country = formValues['consigner-country']?.value
          const countryData = Country.getAllCountries().find(c => c.name === country)
          const isoCode = countryData?.isoCode
          field.options = isoCode
            ? State.getStatesOfCountry(isoCode).map(s => ({
                label: s.name,
                value: s.name
              }))
            : []
          field.type = 'select'
          field.apiUrl = undefined
        }
      })
    })
    return updated
  }
  return (
    <FormBuilder
      setSubmitValues={setSubmitValues}
      formDefaultValue={formDefaultValue}
      config={getModifiedConfig()}
      formGroupName={formGroupName}
      isLoading={isLoading}
      baseUrl={baseUrl}
      onFieldChange={handleFormChange}
    />
  )
}
const disableNonPasswordFields = () => {
  if (typeof window !== 'undefined') {
    const style = document.createElement('style')
    style.textContent = `
      .edit-mode input:not([type="password"]),
      .edit-mode textarea,
      .edit-mode select {
        background-color: #f5f5f5 !important;
        color: #999 !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
        opacity: 0.6 !important;
      }
      .edit-mode .react-select__control,
      .edit-mode .react-select-container,
      .edit-mode [class*="select"] {
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
        pointer-events: none !important;
        opacity: 0.6 !important;
      }
      .edit-mode .react-select__value-container,
      .edit-mode .react-select__single-value {
        color: #999 !important;
      }
      .edit-mode .react-select__indicator {
        color: #999 !important;
      }
      .edit-mode button[type="submit"],
      .edit-mode button[type="reset"] {
        pointer-events: auto !important;
        opacity: 1 !important;
        cursor: pointer !important;
      }
      .edit-mode input[type="password"] {
        background-color: white !important;
        color: inherit !important;
        cursor: text !important;
        pointer-events: auto !important;
        opacity: 1 !important;
      }
    `
    document.head.appendChild(style)
    const formContainer = document.querySelector('form')
    if (formContainer) {
      formContainer.classList.add('edit-mode')
    }
  }
}
const enableAllFields = () => {
  const formContainer = document.querySelector('form')
  if (formContainer) {
    formContainer.classList.remove('edit-mode')
  }
}
interface IChildUserListing extends IChildUserConfig {
  baseUrl: string
}
export function ConsignerOnboarding({
  match,
  location,
  history,
  routesConfig,
  config:pageRenderingConfig
}: IModuleRenderingConfig) {
  const [apiData, setApiData] = React.useState<IRenderControl>({
    data: [],
    message: '',
    isError: false,
    isLoading: false,
    isSuccess: true
  })
  const config=(pageRenderingConfig as any).children['listChildUser'].config
  const {baseUrl}=pageRenderingConfig
  const{label,id}=config
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [selectedDataForEdit, setSelectedDataForEdit] = React.useState<any>({})
  const source = cancelToken().source()
  const crudSource = cancelToken().source()
  const [formSubmitted, setFormSubmitted] = React.useState(false)
  const [formValues, setFormValues] = React.useState<any>({})
  const [edit, setEdit] = React.useState(false)
  const [initialValue, setInitialValue] = React.useState<any>({
    type: "Consigner"
  })
  const { apiConfig, selectedColumns } = config.children['listChildUser']
  const { apiUrl } = apiConfig['listChildUser']
  const { formRenderingConfig, apiConfig: crudConfig } = edit
    ? config.children['editChildUser']
    : config.children['addChildUser']

  React.useEffect(() => {
    fetchData()
    return () => source.cancel('Component unmount in ChildUserListing')
  }, [])

  //NOTE:Invoke when we submit form button.
  React.useEffect(() => {
    if (!isEmpty(formValues.values)) {
      if (edit) {
        const payload = getEditFormValue()
        updateUser(payload)
      } else {
        const payload = getFormValue()
        createUser(payload)
      }
    }
    return () => crudSource.cancel()
  }, [formValues.values])

  //NOTE:Invoke when edit button will be clicked in listing.
  React.useEffect(() => {
    if (!isEmpty(selectedDataForEdit)) {
      setEdit(true)
      disableNonPasswordFields()
      const {
        firstName,
        lastName,
        email,
        state,
        username,
        primaryPhoneNo,
        roles,
        configFilters,
        userTimezone,
        feEmail,
        feUsername,
        language,
        mroleState,
        mroleCity,
        mroleConsignerName,
        mroleConsignerCode,
        mroleConsignerAddress,
        mroleConsignerPhoneNo,
        mroleIsJourneyModel,
        mroleReplicateData,
        mroleDemoAccount,
        mroleSettings,
        mroleConsignerTripSettings,
        useOauth2,
        timeFmt24Hr
      } = selectedDataForEdit
      const clonedInitialValue = {
            firstName,
            lastName,
            email,
            state: { label: state, value: state },
            username,
            primaryPhoneNo,
            roleIds: roles,
            configFilterIds: configFilters,
            userTimezone: { label: userTimezone, value: userTimezone },
            feEmail,
            feUsername,
            language: { label: language, value: language},
            stateLocal: { label: mroleState, value: mroleState},
            city: mroleCity,
            consignerCode: mroleConsignerCode,
            consignerName: mroleConsignerName,
            consignerAddress: mroleConsignerAddress,
            consignerPhoneNo: mroleConsignerPhoneNo,
            fareyeInternalUrl: mroleSettings?.fareyeAlertConnectorUrl || '',
            isJourneyModel: { label: mroleIsJourneyModel, value: mroleIsJourneyModel},
            replicateData: { label: mroleReplicateData, value: mroleReplicateData},
            demoAccount: { label: mroleDemoAccount, value: mroleDemoAccount},
            forceClosureDays: mroleConsignerTripSettings?.forceClosureDays,
            useOauth2,
            timeFmt24Hr
      }
      setInitialValue(clonedInitialValue)
    }
  }, [selectedDataForEdit])

  //NOTE: Fetching Child Users
  async function fetchData() {
    try {
      setApiData({ ...apiData, isLoading: true })
      const { data } = await get(apiUrl, source.token, {
        headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true }
      })
      setApiData({ ...apiData, isLoading: false, data: data.users })
    } catch (ex:any) {
      setApiData({
        ...apiData,
        isLoading: false,
        isError: true,
        message: 'Something went wrong...'
      })
      console.warn('ChildUserListing', ex)
    }
  }

  //NOTE: Creating child user
  async function createUser(payload: any) {
    try {
      setFormSubmitted(true)
      const resp = await post(
        crudConfig.addChildUser.apiUrl,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }
      formValues.resetForm()
      toast.success('User created successfully.')
      fetchData()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  //NOTE: Updating  child user
  async function updateUser(payload: any) {
    try {
      setFormSubmitted(true)
      const resp = await put(
        `${crudConfig.editChildUser.apiUrl}/${selectedDataForEdit.id}`,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }
      toast.success('User updated successfully.')
      fetchData()
      resetEditForm()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  //NOTE: Getting form Values and making payload for form submission.

  function getEditFormValue() {
    const clonedFormValue: any = convertToNestedBasedOnSeparator(cloneDeep(formValues.values),"-")
    return clonedFormValue
  }

  function getFormValue() {
    const clonedFormValue: any = convertToNestedBasedOnSeparator(cloneDeep(formValues.values),"-")

    clonedFormValue.consigner.userAttributes.state=clonedFormValue?.consigner?.userAttributes?.state?.value
    clonedFormValue.consigner.state=clonedFormValue?.consigner?.state?.value
    clonedFormValue.consigner.userAttributes.userTimezone=clonedFormValue?.consigner?.userAttributes?.userTimezone?.value
    clonedFormValue.consigner.userAttributes.language=clonedFormValue?.consigner?.userAttributes?.language?.value
    return clonedFormValue
  }

  function resetEditForm() {
    enableAllFields()
    setInitialValue({})
    setSelectedDataForEdit(null)
    setEdit(false)
  }
  function validateForm(val: any): any {
    return {
      ...(val.email === val.username
        ? { email: '*Enter different Email ID' }
        : {}),
      ...(val.passwordConfirmation !== val.password
        ? { passwordConfirmation: '*Enter Same Password' }
        : {})
    }
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: 10,
        overflow: 'auto'
      }}
    >
      {edit && (
        <Flex m={4} justifyContentFlexEnd>
          <Button mr={10} variant="primary m" onClick={() => resetEditForm()}>
            Add Child
          </Button>
        </Flex>
      )}
      <Flex style={{ marginBottom: 10 }} width={1} bg="#fff">
        <CustomFormWrapper
          setSubmitValues={setFormValues}
          formDefaultValue={initialValue}
          config={formRenderingConfig}
          formGroupName={id}
          isLoading={formSubmitted}
          baseUrl={baseUrl}
          // validate={validateForm}
          edit={edit}
        />
      </Flex>

      <DataListing
        apiData={apiData}
        label={label}
        history={history}
        containerRef={containerRef}
        selectedColumns={selectedColumns ? selectedColumns : []}
        setSelectedDataForEdit={setSelectedDataForEdit}
      />
    </div>
  )
}
